import { useEffect, useState } from 'react';
import { Clock, CheckCircle2, XCircle, FileCheck } from 'lucide-react';
import type { Approval } from '../../types/approvals';
import { useApprovalStore } from '../../stores/approvalStore';
import Spinner from '../common/Spinner';
import StatCard from '../common/StatCard';
import ApprovalFilters from './ApprovalFilters';
import ApprovalKanban from './ApprovalKanban';
import CreateApprovalModal from './CreateApprovalModal';
import ApprovalDetailModal from './ApprovalDetailModal';

export default function ApprovalsPage() {
  const loading = useApprovalStore((s) => s.loading);
  const approvals = useApprovalStore((s) => s.approvals);
  const stats = useApprovalStore((s) => s.stats);
  const fetchApprovals = useApprovalStore((s) => s.fetchApprovals);
  const fetchStats = useApprovalStore((s) => s.fetchStats);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);

  useEffect(() => {
    fetchApprovals();
    fetchStats();
  }, [fetchApprovals, fetchStats]);

  // Keep selectedApproval fresh from the approvals array
  useEffect(() => {
    if (selectedApproval) {
      const fresh = approvals.find((a) => a.id === selectedApproval.id);
      if (!fresh) {
        setSelectedApproval(null);
      }
    }
  }, [approvals, selectedApproval]);

  const pendingCount = stats?.byStatus?.pending ?? 0;
  const approvedCount = stats?.byStatus?.approved ?? 0;
  const rejectedCount = stats?.byStatus?.rejected ?? 0;
  const totalCount = Object.values(stats?.byStatus ?? {}).reduce((sum, n) => sum + n, 0);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-text-primary">Approvals</h1>
        {loading ? <Spinner size="sm" /> : null}
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Pending"
          value={pendingCount}
          icon={<Clock />}
          status="warning"
        />
        <StatCard
          label="Approved"
          value={approvedCount}
          icon={<CheckCircle2 />}
          status="success"
        />
        <StatCard
          label="Rejected"
          value={rejectedCount}
          icon={<XCircle />}
          status="error"
        />
        <StatCard
          label="Total"
          value={totalCount}
          icon={<FileCheck />}
          status="info"
        />
      </div>

      <ApprovalFilters onNewApproval={() => setShowCreate(true)} />
      <ApprovalKanban onApprovalClick={(approval) => setSelectedApproval(approval)} />

      {showCreate ? (
        <CreateApprovalModal onClose={() => setShowCreate(false)} />
      ) : null}

      {selectedApproval ? (
        <ApprovalDetailModal
          approval={selectedApproval}
          onClose={() => setSelectedApproval(null)}
        />
      ) : null}
    </div>
  );
}
